import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  Award,
  XCircle,
  CheckCircle2,
  RotateCcw,
  Timer,
  WifiOff,
  PlayCircle,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { courseService } from '../../services/course.service';
import { useToast } from '../../components/ui/ToastProvider';
import type {
  QuizPayload,
  QuizSubmissionResult,
  QuizStartResponse,
  QuizQuestion,
} from '@edytech/shared/types';

type Phase = 'intro' | 'active' | 'submitting' | 'result' | 'expired';
type Answers = Record<string, number | number[]>;

const fmt = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

/** Extract a stable error code the server sent (e.g. QZ_TIMER_EXPIRED), if any. */
const errorCode = (e: unknown): string | null =>
  axios.isAxiosError(e) ? (e.response?.data as { code?: string } | undefined)?.code ?? null : null;

const errorMessage = (e: unknown): string =>
  axios.isAxiosError(e)
    ? ((e.response?.data as { error?: string } | undefined)?.error ?? e.message)
    : (e as Error)?.message ?? 'Something went wrong';

export const QuizPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<Phase>('intro');
  const [answers, setAnswers] = useState<Answers>({});
  const [session, setSession] = useState<QuizStartResponse | null>(null);
  const [result, setResult] = useState<QuizSubmissionResult | null>(null);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const answersRef = useRef<Answers>({});
  const deadlineRef = useRef<number | null>(null);
  const submittedRef = useRef(false);

  // Keep the ref in sync without touching refs during render (react-hooks/refs).
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  const { data, isLoading, error } = useQuery<QuizPayload>({
    queryKey: ['quiz', slug],
    queryFn: async () => {
      const course = await courseService.getCourseBySlug(slug!);
      return courseService.getQuiz(course.id);
    },
    enabled: !!slug,
  });

  const startMutation = useMutation({
    mutationFn: async () => {
      const course = await courseService.getCourseBySlug(slug!);
      return courseService.startQuizAttempt(course.id);
    },
    onSuccess: (res) => {
      setSession(res);
      setAnswers({});
      setResult(null);
      setSubmitError(null);
      submittedRef.current = false;
      deadlineRef.current = Date.now() + res.attempt.remainingMs;
      setRemainingMs(res.attempt.remainingMs);
      setPhase('active');
      if (res.live) showToast('Resumed your in-progress attempt', 'warning');
    },
    onError: (e: unknown) => showToast(errorMessage(e), 'error'),
  });

  const submitMutation = useMutation({
    mutationFn: async (payload: { attemptId: number; answers: Answers }) => {
      const course = await courseService.getCourseBySlug(slug!);
      return courseService.submitQuizAttempt(course.id, payload.attemptId, payload.answers);
    },
    onSuccess: (res) => {
      submittedRef.current = true;
      setResult(res);
      setSubmitError(null);
      setPhase('result');
      queryClient.invalidateQueries({ queryKey: ['quiz', slug] });
      if (res.passed) showToast(`Congratulations! You passed with ${res.score}%`, 'success');
      else showToast(`You scored ${res.score}%. Review and try again.`, 'warning');
    },
    onError: (e: unknown) => {
      if (errorCode(e) === 'QZ_TIMER_EXPIRED') {
        submittedRef.current = true;
        setPhase('expired');
        return;
      }
      // Network interruption: answers are kept locally and the attempt is idempotent
      // server-side, so the user can retry without any risk of a double submission.
      submittedRef.current = false;
      setSubmitError(errorMessage(e));
      setPhase('active');
    },
  });

  const doSubmit = useCallback(() => {
    if (submittedRef.current || !session) return;
    submittedRef.current = true; // guard against double-fire (auto + manual)
    setPhase('submitting');
    submitMutation.mutate({ attemptId: session.attempt.id, answers: answersRef.current });
  }, [session, submitMutation]);

  // Countdown engine — the deadline is anchored to the server's remainingMs at T-start,
  // so local clock drift never extends the window. Auto-submits held answers at 0.
  useEffect(() => {
    if (phase !== 'active' || deadlineRef.current == null || session?.quiz.timeLimitMinutes == null) return;
    const tick = window.setInterval(() => {
      const left = deadlineRef.current! - Date.now();
      setRemainingMs(left);
      if (left <= 0 && !submittedRef.current) {
        window.clearInterval(tick);
        showToast("Time's up — submitting your answers", 'warning');
        doSubmit();
      }
    }, 500);
    return () => window.clearInterval(tick);
  }, [phase, session, doSubmit, showToast]);

  const selectAnswer = (questionId: number, optionIndex: number, type: string) => {
    if (phase !== 'active') return;
    setAnswers((prev) => {
      if (type === 'multiple') {
        const current = (prev[String(questionId)] as number[]) || [];
        if (current.includes(optionIndex)) {
          return { ...prev, [String(questionId)]: current.filter((i) => i !== optionIndex) };
        }
        return { ...prev, [String(questionId)]: [...current, optionIndex].sort((a, b) => a - b) };
      }
      return { ...prev, [String(questionId)]: optionIndex };
    });
  };

  const onRetry = () => {
    setSession(null);
    setResult(null);
    setAnswers({});
    setRemainingMs(null);
    setSubmitError(null);
    deadlineRef.current = null;
    submittedRef.current = false;
    setPhase('intro');
    queryClient.invalidateQueries({ queryKey: ['quiz', slug] });
  };


  if (isLoading) return <div className="py-20 text-center text-[rgb(var(--light-secondary))]">Loading quiz...</div>;
  if (error || !data) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-2xl font-bold">Quiz unavailable</h1>
        <Link to={`/courses/${slug}`} className="mt-4 inline-block font-medium text-primary hover:underline">Back to course</Link>
      </div>
    );
  }

  const quizMeta = session?.quiz ?? data.quiz;
  const questions: QuizQuestion[] = session?.questions ?? data.questions;
  const timed = quizMeta.timeLimitMinutes != null;
  const attemptsLeft = quizMeta.maxAttempts - (data.quiz.attemptsUsed ?? 0);
  const hasLiveAttempt = !!data.liveAttempt;
  const canStart = attemptsLeft > 0 || hasLiveAttempt;
  const allAnswered = questions.every((q) => answers[String(q.id)] != null);
  const totalMs = quizMeta.timeLimitMinutes != null ? quizMeta.timeLimitMinutes * 60 * 1000 : null;
  const urgent = remainingMs != null && remainingMs <= 60_000;
  const warning = remainingMs != null && totalMs != null && remainingMs <= totalMs * 0.25;

  return (
    <div className="container mx-auto max-w-3xl px-4 py-12">
      <Link to={`/courses/${slug}`} className="text-sm font-medium text-primary hover:underline">← Back to course</Link>

      {/* ---- INTRO ---- */}
      {phase === 'intro' && (
        <div className="mt-6 rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-8">
          <h1 className="text-2xl font-bold">{quizMeta.title}</h1>
          {quizMeta.description && <p className="mt-2 text-[rgb(var(--light-secondary))]">{quizMeta.description}</p>}
          <dl className="mt-6 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            <div><dt className="text-[rgb(var(--light-secondary))]">Questions</dt><dd className="font-semibold">{questions.length}</dd></div>
            <div><dt className="text-[rgb(var(--light-secondary))]">Pass mark</dt><dd className="font-semibold">{quizMeta.passingScore}%</dd></div>
            <div><dt className="text-[rgb(var(--light-secondary))]">Time limit</dt><dd className="font-semibold">{timed ? `${quizMeta.timeLimitMinutes} min` : 'Untimed'}</dd></div>
            <div><dt className="text-[rgb(var(--light-secondary))]">Attempts</dt><dd className="font-semibold">{Math.max(0, attemptsLeft)} / {quizMeta.maxAttempts} left</dd></div>
          </dl>

          {data.submission && (
            <div className={`mt-6 rounded-lg border p-4 ${data.submission.passed ? 'border-green-500/40 bg-green-500/10' : 'border-[rgb(var(--border))]'}`}>
              <p className="text-sm">
                Previous best: <span className="font-semibold">{data.submission.score}%</span>
                {data.submission.passed ? ' — passed ✓' : ' — not passed'}
              </p>
            </div>
          )}

          {timed && (
            <p className="mt-4 flex items-center gap-2 text-sm text-amber-600">
              <Timer className="h-4 w-4" /> The timer starts when you begin and cannot be paused.
            </p>
          )}

          <div className="mt-6">
            {canStart ? (
              <Button onClick={() => startMutation.mutate()} disabled={startMutation.isPending}>
                <PlayCircle className="mr-2 h-4 w-4" />
                {startMutation.isPending ? 'Starting...' : hasLiveAttempt ? 'Resume quiz' : data.submission ? 'Retry quiz' : 'Start quiz'}
              </Button>
            ) : (
              <p className="flex items-center gap-2 text-sm text-red-500">
                <AlertTriangle className="h-4 w-4" /> No attempts remaining for this quiz.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ---- EXPIRED ---- */}
      {phase === 'expired' && (
        <div className="mt-6 rounded-xl border border-red-500/40 bg-red-500/10 p-8 text-center">
          <Timer className="mx-auto h-14 w-14 text-red-500" />
          <h2 className="mt-4 text-2xl font-bold">Time expired</h2>
          <p className="mt-2 text-sm text-[rgb(var(--light-secondary))]">
            The time limit was reached before your answers could be submitted.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button variant="secondary" onClick={onRetry}><RotateCcw className="mr-2 h-4 w-4" /> Back to quiz info</Button>
            <Button onClick={() => (window.location.href = '/dashboard/courses')}>Go to My Courses</Button>
          </div>
        </div>
      )}

      {/* ---- RESULT ---- */}
      {phase === 'result' && result && (
        <SubmissionView result={result} quiz={quizMeta} questions={questions} attemptsLeft={attemptsLeft} onRetry={onRetry} />
      )}


      {/* ---- ACTIVE QUIZ ---- */}
      {(phase === 'active' || phase === 'submitting') && session && (
        <>
          {/* Sticky timer rail */}
          {timed && (
            <div className={`sticky top-0 z-10 -mx-4 mb-6 flex items-center justify-between border-b px-4 py-3 backdrop-blur ${
              urgent ? 'border-red-500/40 bg-red-500/10' : warning ? 'border-amber-500/40 bg-amber-500/10' : 'border-[rgb(var(--border))] bg-[rgb(var(--card))]/90'
            }`}>
              <span className="flex items-center gap-2 text-sm font-medium">
                <Timer className={`h-5 w-5 ${urgent ? 'animate-pulse text-red-500' : warning ? 'text-amber-500' : 'text-primary'}`} />
                Time remaining
              </span>
              <span className={`font-mono text-lg font-bold ${urgent ? 'text-red-500' : warning ? 'text-amber-600' : ''}`}>
                {remainingMs != null ? fmt(remainingMs) : '--:--'}
              </span>
            </div>
          )}

          {submitError && (
            <div className="mb-6 flex items-center justify-between rounded-lg border border-amber-500/40 bg-amber-500/10 p-4">
              <p className="flex items-center gap-2 text-sm text-amber-700">
                <WifiOff className="h-4 w-4 shrink-0" />
                Submission interrupted — your answers are safe. Retry now.
              </p>
              <Button size="sm" variant="outline" onClick={doSubmit} disabled={submitMutation.isPending}>
                {submitMutation.isPending ? 'Retrying...' : 'Retry submit'}
              </Button>
            </div>
          )}

          <div className="mb-6 flex items-center justify-between">
            <p className="text-sm text-[rgb(var(--light-secondary))]">
              Quiz: <span className="font-medium text-[rgb(var(--foreground))]">{quizMeta.title}</span>
            </p>
            <p className="text-sm text-[rgb(var(--light-secondary))]">
              {Object.keys(answers).length} of {questions.length} answered
            </p>
          </div>

          <div className="space-y-6">
            {questions.map((q, qi) => {
              const current = answers[String(q.id)];
              const selected = Array.isArray(current) ? current : current != null ? [current] : [];
              return (
                <fieldset key={q.id} className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-5" disabled={phase === 'submitting'}>
                  <legend className="mb-3 px-1 text-sm font-medium">
                    Question {qi + 1} <span className="text-[rgb(var(--light-secondary))]">· {q.points} pt{q.points === 1 ? '' : 's'}</span>
                  </legend>
                  <p className="mb-4 font-medium">{q.prompt}</p>
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => {
                      const isSel = selected.includes(oi);
                      const inputType = q.type === 'multiple' ? 'checkbox' : 'radio';
                      return (
                        <label
                          key={oi}
                          className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition ${
                            isSel ? 'border-primary bg-primary/10' : 'border-[rgb(var(--border))] hover:bg-[rgb(var(--border))]/40'
                          }`}
                        >
                          <input
                            type={inputType}
                            name={q.type === 'multiple' ? `q-${q.id}-${oi}` : `q-${q.id}`}
                            checked={isSel}
                            onChange={() => selectAnswer(q.id, oi, q.type)}
                            className="mt-1 accent-[rgb(var(--primary))]"
                          />
                          <span className="text-sm">{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              );
            })}
          </div>


          <div className="mt-8 flex flex-col items-center gap-3">
            <Button size="lg" onClick={doSubmit} disabled={phase === 'submitting' || !allAnswered}>
              {phase === 'submitting' ? 'Submitting...' : 'Submit Quiz'}
            </Button>
            {!allAnswered && (
              <p className="text-center text-xs text-[rgb(var(--light-secondary))]">Answer all questions to submit</p>
            )}
            {phase === 'submitting' && (
              <p className="text-center text-xs text-[rgb(var(--light-secondary))]">Your answers are being submitted securely…</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const SubmissionView: React.FC<{
  result: QuizSubmissionResult;
  quiz: { title: string; passingScore: number };
  questions: QuizQuestion[];
  attemptsLeft: number;
  onRetry: () => void;
}> = ({ result, quiz, questions, attemptsLeft, onRetry }) => {
  const Icon = result.passed ? Award : XCircle;
  return (
    <div className="mt-6">
      <div className={`rounded-xl border p-8 text-center ${
        result.passed ? 'border-green-500/40 bg-green-500/10' : 'border-red-500/40 bg-red-500/10'
      }`}>
        <Icon className={`mx-auto h-16 w-16 ${result.passed ? 'text-green-500' : 'text-red-500'}`} />
        <h2 className="mt-4 text-2xl font-bold">
          {result.passed ? 'Congratulations — You Passed!' : 'Quiz Not Passed'}
        </h2>
        <p className="mt-2 text-lg">
          Your score: <span className="font-bold">{result.score}%</span> (Pass mark: {quiz.passingScore}%)
        </p>
        {result.passed ? (
          <p className="mt-2 text-sm text-green-600">Your certificate will be issued when all lessons are complete.</p>
        ) : (
          <p className="mt-2 text-sm text-[rgb(var(--light-secondary))]">Review the course material and try again.</p>
        )}
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {!result.passed && attemptsLeft > 0 && (
            <Button variant="secondary" onClick={onRetry}>
              <RotateCcw className="mr-2 h-4 w-4" /> Retry Quiz
            </Button>
          )}
          <Button onClick={() => (window.location.href = '/dashboard/courses')}>Go to My Courses</Button>
        </div>
      </div>

      {result.results.length > 0 && (
        <div className="mt-8 space-y-4">
          <h3 className="text-lg font-semibold">Review Answers</h3>
          {questions.map((q, qi) => {
            const r = result.results.find((rr) => rr.questionId === q.id);
            return (
              <div key={q.id} className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-5">
                <div className="flex items-start gap-2">
                  {r?.correct ? (
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
                  ) : (
                    <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
                  )}
                  <div>
                    <p className="font-medium">{qi + 1}. {q.prompt}</p>
                    {r && (
                      <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">
                        Correct answer:{' '}
                        <span className="font-medium text-[rgb(var(--foreground))]">
                          {(Array.isArray(r.correctAnswer) ? r.correctAnswer : [r.correctAnswer])
                            .map((i) => q.options[i])
                            .join(', ')}
                        </span>
                      </p>
                    )}
                    {r?.explanation && <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">{r.explanation}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

